import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../config";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";

const normalizeImage = (path) => {
  if (!path) return "/assets/img/a1-img/avatar-placeholder.png";
  if (typeof path !== "string") return "/assets/img/a1-img/avatar-placeholder.png";

  const cleanPath = path.trim();
  if (cleanPath.startsWith("http://") || cleanPath.startsWith("https://")) {
    return cleanPath;
  }
  if (cleanPath.startsWith("//")) {
    return `https:${cleanPath}`;
  }
  if (
    cleanPath.startsWith("dsdxwhpuu/") ||
    cleanPath.startsWith("image/upload/") ||
    cleanPath.includes("cloudinary.com")
  ) {
    const withoutHost = cleanPath.replace(/^https?:\/\/res\.cloudinary\.com\/?/, "").replace(/^\/+/, "");
    return `https://res.cloudinary.com/${withoutHost}`;
  }
  if (cleanPath.startsWith("assets/") || cleanPath.startsWith("/assets/")) {
    return cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`;
  }

  const normalized = cleanPath.replace(/\\/g, "/").replace(/^\/+/, "");
  return `${API_BASE_URL}/${normalized}`;
};

export default function OrderSuccess() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        const token =
          localStorage.getItem("token") || sessionStorage.getItem("token");

        const headers = token ? { "x-auth-token": token } : {};

        const { data } = await axios.get(
          `${API_BASE_URL}/api/orders/${orderId}`,
          { headers },
        );
        setOrder(data);
      } catch (err) {
        console.error("Error fetching order:", err);
      } finally {
        setLoading(false);
      }
    };

    if (orderId) fetchOrderDetails();
  }, [orderId]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "Today";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const handleCopy = () => {
    if (order?._id || orderId) {
      navigator.clipboard.writeText(order?._id || orderId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div
        className="container py-5 d-flex justify-content-center align-items-center"
        style={{ minHeight: "80vh" }}
      >
        <div className="spinner-border text-danger" role="status"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div>
        <Navbar />
        <div className="container py-5 text-center my-5">
          <h3 className="fw-bold">Order not found</h3>
          <p className="text-muted">We couldn&apos;t retrieve the details for this order.</p>
          <Link to="/categories" className="bd-fill__btn mt-3 d-inline-block">
            Back to Shop
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const shortId = String(order._id).slice(-8).toUpperCase();

  return (
    <div>
      <Navbar />
      <div className="bg-light min-vh-100 py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-9">

              {/* Success Header Card (Original Style) */}
              <div className="card border-0 shadow-sm rounded-4 mb-4 overflow-hidden">
                <div className="card-body p-4 p-md-5">
                  <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-4">
                    <div className="d-flex flex-column flex-md-row align-items-center gap-4 text-center text-md-start">
                      <div className="rounded-circle bg-success bg-opacity-10 p-3 flex-shrink-0">
                        <i className="fa-solid fa-check text-success fs-1"></i>
                      </div>
                      <div>
                        <h1 className="fw-bold text-dark mb-1">
                          Order Placed, Thank You!
                        </h1>
                        <p className="text-muted mb-0">
                          A confirmation email has been sent to{" "}
                          <strong>{order.email}</strong>
                        </p>
                      </div>
                    </div>

                    <div className="text-center text-md-end flex-shrink-0">
                      <span className="text-muted small d-block mb-1">Order ID</span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger rounded-pill px-3 py-1 fw-bold font-monospace"
                        onClick={handleCopy}
                        title="Copy Order ID"
                      >
                        #{shortId}{" "}
                        <i className={`fa-solid ${copied ? "fa-check text-success" : "fa-copy"} ms-1`}></i>
                      </button>
                      {copied && (
                        <small className="text-success d-block mt-1 fw-semibold">Copied!</small>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="row g-4">
                {/* Left Column: Shipment & Items */}
                <div className="col-md-8">
                  {/* Tracking Info Card (Original Style) */}
                  <div className="card border-0 shadow-sm rounded-4 mb-4">
                    <div className="card-body p-4">
                      <h5 className="fw-bold mb-4 d-flex align-items-center gap-2">
                        <i className="fa-solid fa-truck-fast text-danger"></i>
                        Shipment Updates
                      </h5>
                      <div className="p-3 bg-light rounded-3 border-start border-danger border-4">
                        <p className="fw-bold mb-1">
                          {order.awbCode
                            ? "Arriving Soon"
                            : "Preparing Shipment"}
                        </p>
                        <p className="small text-muted mb-0">
                          {order.awbCode
                            ? `Your package is being handled by ${order.courierName || "Express Courier"}.`
                            : "We are packing your treats with care and will dispatch them shortly."}
                        </p>
                        <p className="smaller text-muted mt-2 mb-0">
                          Order Date: <strong>{formatDate(order.createdAt)}</strong>
                        </p>
                        {order.awbCode && (
                          <div className="mt-3 d-flex align-items-center gap-3">
                            <div className="small bg-white px-3 py-1 rounded-pill border fw-bold">
                              Tracking: {order.awbCode}
                            </div>
                            <a
                              href={`https://shiprocket.co/tracking/${order.awbCode}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-danger small fw-bold text-decoration-none"
                            >
                              Track Live{" "}
                              <i className="fa-solid fa-arrow-up-right-from-square ms-1"></i>
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Order Items List (Original Style) */}
                  <div className="card border-0 shadow-sm rounded-4">
                    <div className="card-body p-4">
                      <h5 className="fw-bold mb-4">Items in this shipment</h5>
                      <div className="d-flex flex-column gap-3">
                        {order.items?.map((item, idx) => (
                          <div
                            key={idx}
                            className="d-flex gap-3 pb-3 border-bottom last-child-border-0 align-items-center"
                          >
                            <div
                              className="flex-shrink-0"
                              style={{ width: "90px", height: "60px" }}
                            >
                              <img
                                src={normalizeImage(item.image || item.img)}
                                alt={item.name}
                                className="img-fluid rounded-3 object-fit-cover h-100 w-100 border"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "/assets/img/a1-img/placeholder.png";
                                }}
                              />
                            </div>
                            <div className="flex-grow-1">
                              <h6 className="fw-bold mb-1">{item.name}</h6>
                              <p className="small text-muted mb-0">
                                Quantity: {item.quantity} {item.net_wt ? `| Net Wt: ${item.net_wt}` : ""}
                              </p>
                            </div>
                            <div className="text-end">
                              <p className="fw-bold text-danger mb-0">
                                ₹{Math.round(item.price * item.quantity)}
                              </p>
                              {item.quantity > 1 && (
                                <small className="text-muted">₹{Math.round(item.price)} each</small>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column: Address, Order Summary & Actions */}
                <div className="col-md-4">
                  {/* Delivery Address Card (Original Style) */}
                  <div className="card border-0 shadow-sm rounded-4 mb-4">
                    <div className="card-body p-4 text-start">
                      <h6 className="fw-bold small text-uppercase text-muted mb-3">
                        Delivery Address
                      </h6>
                      <p className="fw-bold mb-1">{order.customerName}</p>
                      <p className="small text-muted mb-1">
                        {order.shippingAddress?.addressLine1}
                      </p>
                      {order.shippingAddress?.addressLine2 && (
                        <p className="small text-muted mb-1">
                          {order.shippingAddress?.addressLine2}
                        </p>
                      )}
                      <p className="small text-muted mb-3">
                        {order.shippingAddress?.city},{" "}
                        {order.shippingAddress?.state} -{" "}
                        {order.shippingAddress?.pincode}
                      </p>
                      {order.phone && (
                        <p className="small fw-bold mb-0">
                          <i className="fa-solid fa-phone-volume me-2 text-danger"></i>
                          {order.phone}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Order Total Breakdown */}
                  <div className="card border-0 shadow-sm rounded-4 mb-4">
                    <div className="card-body p-4 text-start">
                      <h6 className="fw-bold small text-uppercase text-muted mb-3">
                        Payment Summary
                      </h6>
                      <div className="d-flex justify-content-between mb-2 small">
                        <span className="text-muted">Subtotal</span>
                        <span className="fw-bold">₹{Math.round(order.subtotal || 0)}</span>
                      </div>
                      <div className="d-flex justify-content-between mb-2 small">
                        <span className="text-muted">Delivery Charge</span>
                        <span className={`fw-bold ${order.shippingCharge === 0 ? "text-success" : ""}`}>
                          {order.shippingCharge === 0 ? "FREE" : `₹${Math.round(order.shippingCharge)}`}
                        </span>
                      </div>
                      {order.couponCode && (
                        <div className="d-flex justify-content-between mb-2 small text-success">
                          <span>Discount ({order.couponCode})</span>
                          <span className="fw-bold">
                            - ₹{Math.round(Math.max(0, (order.subtotal + (order.shippingCharge || 0)) - order.totalAmount))}
                          </span>
                        </div>
                      )}
                      <hr className="my-2 opacity-10" />
                      <div className="d-flex justify-content-between mb-2">
                        <span className="fw-bold">Total Paid</span>
                        <span className="fw-bold text-danger fs-5">₹{Math.round(order.totalAmount || 0)}</span>
                      </div>
                      <div className="bg-light p-2 rounded-2 small text-muted d-flex justify-content-between align-items-center mt-2">
                        <span>Payment Status</span>
                        <span className="badge bg-success bg-opacity-10 text-white fw-bold">PAID ✓</span>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons (Original Site Buttons) */}
                  <div className="d-flex flex-column gap-3">
                    <Link
                      to="/categories"
                      className="bd-fill__btn w-100 text-center py-3 text-decoration-none"
                    >
                      Continue Shopping
                    </Link>
                    <Link
                      to="/profile"
                      className="bd-unfill__btn w-100 text-center py-3 text-decoration-none"
                    >
                      View Order History
                    </Link>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
